"use client";

import { FunctionComponent } from "react";
import { SidebarSkeleton } from "@components/layout/sidebar/sidebar-skeleton";
import { useQuery } from "@apollo/client";
import { GQLMeQuery, GQLMeQueryVariables } from "queries/user.generated";
import { ME_QUERY } from "queries/user";
import { SidebarMenu } from "@components/common/sidebar-menu/sidebar-menu";
import { Footer } from "@components/footer/footer";
import { InstagramColor } from "@components/common/icons/instagram-color";
import { TikTokColor } from "@components/common/icons/tik-tok-color";
import { YouTubeColor } from "@components/common/icons/you-tube-color";
import { PinterestColor } from "@components/common/icons/pinterest-color";
import { AppleMusicColor } from "@components/common/icons/apple-music-color";
import { SpotifyColor } from "@components/common/icons/spotify-color";
import { TwitchColor } from "@components/common/icons/twitch-color";
import { TwitterColor } from "@components/common/icons/twitter-color";
import { Close } from "@components/common/close/close";
import { useRouter } from "@components/common/hooks/router";
import { useRouter as nextRouter } from "next/dist/client/router";
import { GQLChannel, GQLPostType } from "graphql.generated";
import { Camera } from "@components/common/icons/camera";
import { Video } from "@components/common/icons/video";
import { Guide } from "@components/common/icons/guide";
import { IdentityVerification } from "@components/common/icons/identity-verification";
import { PlayCircle } from "@components/common/icons/play-circle";
import { Podcast } from "@components/common/icons/podcast";
import { Tag } from "@components/common/icons/tag";
import { useTheme } from "@components/common/hooks/theme";
import { Instagram } from "@components/common/icons/instagram";
import { TikTok } from "@components/common/icons/tik-tok";
import { YouTube } from "@components/common/icons/you-tube";
import { Pinterest } from "@components/common/icons/pinterest";
import { AppleMusic } from "@components/common/icons/apple-music";
import { Spotify } from "@components/common/icons/spotify";
import { Twitch } from "@components/common/icons/twitch";
import { Twitter } from "@components/common/icons/twitter";
import { resolveChannelName } from "@utils/channel-resolver";

export interface SidebarProps {
    expanded: boolean;
}

export const Sidebar: FunctionComponent<SidebarProps> = ({ expanded }) => {
    const { data: dataMe, loading: loadingMe } = useQuery<GQLMeQuery, GQLMeQueryVariables>(ME_QUERY);
    const { filters, toggleFilter } = useRouter();
    const { mode } = useTheme();
    const { pathname } = nextRouter();
    const disableChannels = pathname === "/search" && !filters.tab;
    const disablePostTypes = pathname === "/search";
    const disableHashtags = pathname === "/search";
    const disableInterests = pathname === "/search";

    if (loadingMe || !dataMe?.me) return <SidebarSkeleton expanded={expanded} />;

    return (
        <div className="pt-8">
            <div className="mb-6 border-b border-gray-100 pb-6">
                {expanded && <div className="px-2 pb-2 text-xs font-semibold">Meine Netzwerke</div>}
                <SidebarMenu
                    expanded={expanded}
                    items={[
                        {
                            leftAdornments: mode === "dark" ? <Instagram /> : <InstagramColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Instagram) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Instagram),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Instagram)}`,
                            },
                            disabled: disableChannels,
                        },
                        {
                            leftAdornments: mode === "dark" ? <TikTok /> : <TikTokColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Tiktok) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Tiktok),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Tiktok)}`,
                            },
                            disabled: disableChannels,
                        },
                        {
                            leftAdornments: mode === "dark" ? <YouTube /> : <YouTubeColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Youtube) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Youtube),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Youtube)}`,
                            },
                            disabled: disableChannels,
                        },
                        {
                            leftAdornments: mode === "dark" ? <Pinterest /> : <PinterestColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Pinterest) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Pinterest),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Pinterest)}`,
                            },
                            disabled: disableChannels,
                        },
                        {
                            leftAdornments: mode === "dark" ? <AppleMusic /> : <AppleMusicColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Apple) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Apple),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Apple)}`,
                            },
                            disabled: disableChannels,
                        },
                        {
                            leftAdornments: mode === "dark" ? <Spotify /> : <SpotifyColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Spotify) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Spotify),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Spotify)}`,
                            },
                            disabled: disableChannels,
                        },
                        {
                            leftAdornments: mode === "dark" ? <Twitch /> : <TwitchColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Twitch) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Twitch),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Twitch)}`,
                            },
                            disabled: disableChannels,
                        },
                        {
                            leftAdornments: mode === "dark" ? <Twitter /> : <TwitterColor />,
                            rightAdornments: filters.ch?.includes(GQLChannel.Twitter) ? <Close color="white" component="div" /> : undefined,
                            label: resolveChannelName(GQLChannel.Twitter),
                            link: {
                                href: `${toggleFilter("ch", GQLChannel.Twitter)}`,
                            },
                            disabled: disableChannels,
                        },
                    ]}
                />
            </div>
            <div className="mb-6 border-b border-gray-100 pb-6">
                {expanded && <div className="px-2 pb-2 text-xs font-semibold">Beitragstypen</div>}
                <SidebarMenu
                    expanded={expanded}
                    items={[
                        {
                            leftAdornments: <Video />,
                            rightAdornments: filters.pt?.includes(GQLPostType.Video) ? <Close color="white" component="div" /> : undefined,
                            label: "Videos",
                            link: {
                                href: `${toggleFilter("pt", GQLPostType.Video)}`,
                            },
                            disabled: disablePostTypes,
                        },
                        {
                            leftAdornments: <Camera />,
                            rightAdornments: filters.pt?.includes(GQLPostType.Image) ? <Close color="white" component="div" /> : undefined,
                            label: "Bilder",
                            link: {
                                href: `${toggleFilter("pt", GQLPostType.Image)}`,
                            },
                            disabled: disablePostTypes,
                        },
                        {
                            leftAdornments: <PlayCircle />,
                            rightAdornments: filters.pt?.includes(GQLPostType.Track) ? <Close color="white" component="div" /> : undefined,
                            label: "Music",
                            link: {
                                href: `${toggleFilter("pt", GQLPostType.Track)}`,
                            },
                            disabled: disablePostTypes,
                        },
                        {
                            leftAdornments: <Guide />,
                            rightAdornments: filters.pt?.includes(GQLPostType.News) ? <Close color="white" component="div" /> : undefined,
                            label: "News",
                            link: {
                                href: `${toggleFilter("pt", GQLPostType.News)}`,
                            },
                            disabled: disablePostTypes,
                        },
                        {
                            leftAdornments: <Podcast />,
                            rightAdornments: filters.pt?.includes(GQLPostType.Podcast) ? <Close color="white" component="div" /> : undefined,
                            label: "Podcasts",
                            link: {
                                href: `${toggleFilter("pt", GQLPostType.Podcast)}`,
                            },
                            disabled: disablePostTypes,
                        },
                        {
                            leftAdornments: <IdentityVerification />,
                            rightAdornments: filters.pt?.includes(GQLPostType.Live) ? <Close color="white" component="div" /> : undefined,
                            label: "Livestreams",
                            link: {
                                href: `${toggleFilter("pt", GQLPostType.Live)}`,
                            },
                            disabled: disablePostTypes,
                        },
                        {
                            leftAdornments: <Video />,
                            rightAdornments: filters.pt?.includes(GQLPostType.Movie) ? <Close color="white" component="div" /> : undefined,
                            label: "Movie",
                            link: {
                                href: `${toggleFilter("pt", GQLPostType.Movie)}`,
                            },
                            disabled: disablePostTypes,
                        },
                    ]}
                />
            </div>
            <div className="mb-6 border-b border-gray-100 pb-6">
                {expanded && <div className="px-2 pb-2 text-xs font-semibold">Hashtags</div>}
                <SidebarMenu
                    expanded={expanded}
                    items={dataMe.me.hashtags.map((item) => {
                        return {
                            leftAdornments: <Tag />,
                            rightAdornments: filters.ht?.includes(item) ? <Close color="white" component="div" /> : undefined,
                            label: item,
                            link: {
                                href: `${toggleFilter("ht", item)}`,
                            },
                            disabled: disableHashtags,
                        };
                    })}
                />
            </div>
            <div className="mb-6 border-b border-gray-100 pb-6">
                {expanded && <div className="px-2 pb-2 text-xs font-semibold">Interessen</div>}
                <SidebarMenu
                    expanded={expanded}
                    items={dataMe.me.interests.map((item) => {
                        return {
                            leftAdornments: <Tag />,
                            rightAdornments: filters.in?.includes(item.name) ? <Close color="white" component="div" /> : undefined,
                            label: item.name,
                            link: {
                                href: `${toggleFilter("in", item.name)}`,
                            },
                            disabled: disableInterests,
                        };
                    })}
                />
            </div>
            {/*
            <div className="mb-6 border-b border-gray-100 pb-6">
                {expanded && <div className="px-2 pb-2 text-xs font-semibold">Meine Interessen</div>}
                <SidebarMenu
                    expanded={expanded}
                    items={[
                        { leftAdornments: <Home />, label: "Pets", link: { href: "/" } },
                        { leftAdornments: <Api />, label: "Auto", link: { href: "/" } },
                        { leftAdornments: <Api />, label: "Luxury", link: { href: "/" } },
                        { leftAdornments: <Api />, label: "Sport", link: { href: "/" } },
                    ]}
                />
            </div>
            <div className="mb-6 border-b border-gray-100 pb-6">
                {expanded && <div className="px-2 pb-2 text-xs font-semibold">Meine Hashtags</div>}
                <SidebarMenu
                    expanded={expanded}
                    items={[
                        { leftAdornments: <Home />, label: "#cars", link: { href: "/" } },
                        { leftAdornments: <Api />, label: "#sports", link: { href: "/" } },
                    ]}
                />
            </div>
            <div className="border-b border-gray-100 pb-6">
                {expanded && <div className="px-2 pb-2 text-xs font-semibold">Settings</div>}
                <SidebarMenu
                    expanded={expanded}
                    items={[
                        { leftAdornments: <Home />, label: "Home", link: { href: "/" } },
                        { leftAdornments: <Api />, label: "Testing", link: { href: "/testing" } },
                        {
                            leftAdornments: <Business />,
                            label: "Components",
                            children: [
                                { label: "Single component", link: { href: "/single-component-testing" } },
                                { label: "Lorem ipsum" },
                                { label: "Dolor sit amet" },
                                { label: "Lorem" },
                            ],
                        },
                        {
                            leftAdornments: <Chat />,
                            label: "Contact",
                            children: [{ label: "Dolor sit amet" }, { label: "Lorem" }, { label: "Lorem ipsum" }],
                        },
                    ]}
                />
            </div>
            */}
            {expanded && <Footer />}
        </div>
    );
};
